/**
 * store/modules/user.js
 *
 * Vuex module handling user state and actions.
 */
import axios from 'axios'

export default {
  // User state.
  state: {
    authenticated: false,
    user: {},
    profile: {},
    userIntegrations: [],
    userIntegreation: {},
    walletBalance: 0,
    transactionHistory: [],
    loginRedirect: '',
    plaidLink: null
  },

  // Mutations to alter user state.
  mutations: {
    setAuth: (state, authenticated) => (state.authenticated = authenticated),
    setUser: (state, user) => (state.user = user),
    setProfile: (state, profile) => (state.profile = profile),
    setPlaidLink: (state, link) => (state.plaidLink = link),
    setUserIntegration: (state, integreation) => (state.userIntegreation = integreation),
    setUserIntegrations: (state, integrations) => (state.userIntegrations = integrations),
    setWalletBalance: (state, balance) => (state.walletBalance = balance),
    setTransactionHistory: (state, history) => (state.transactionHistory = history),
    setLoginRedirect: (state, url) => (state.loginRedirect = url),
  },

  // Actions handling requests, and committing mutations.
  actions: {
    // Check if the user is logged in and set the user object if so.
    async getAuthState ({ commit, dispatch }) {
      try {
        let response = await axios.get('/api/user/me')
        // We're authenticated, set auth, user, and logout url.
        commit('setAuth', true)
        commit('setUser', response.data)
      } catch {
        // Not logged in, set auth, and login url.
        commit('setAuth', false)
        commit('setUser', {})
        dispatch('getLoginRedirect')
      }
    },

    // Fetch the login redirect url.
    async getLoginRedirect ({ commit }) {
      try {
        let response = await axios.get('/api/user/redirect')
        commit('setLoginRedirect', response.data.url)
      } catch (error) {
        console.error('Unable to fetch login redirect.')
        console.error(error);
      }
    },

    // Authenticate with the backend with the given token.
    async login ({ commit, dispatch }, authToken) {
      try {
        let response = await axios.get('/api/user/login?id_token=' + authToken)
        commit('setAuth', true)
        commit('setUser', response.data)
        await dispatch('getProfile')
      } catch (error) {
        console.error('Login Failed')
        throw error
      }
    },

    // Fetch the logout redirect url.
    async logout ({ commit, dispatch }) {
      try {
        // There is a return for this enpoint that we potentially may
        // need in the future. For now we just call the endpoint.
        await axios.get('/api/user/logout')
        // Update the auth state to reflect that we logged out.
        commit('setAuth', false)
        dispatch('getLoginRedirect')
      } catch (error) {
        console.error('Unable to logout.')
        console.error(error);
      }
    },

    // Fetch profile data.
    async getProfile ({ commit, dispatch }) {
      console.log('getting profile')
      try {
        let response = await axios.get('/api/user/profile')
        console.log(response.data)
        commit('setProfile', response.data)
        await dispatch('getUserPlaidLinkToken')
      } catch (error) {
        console.error('Unable to fetch profile.')
        dispatch('getAuthState')
      }
    },

    // Update profile.
    async updateUserProfile ({ commit, dispatch }, profile = {}) {
      try {
        let response = await axios.put('/api/user/profile', profile)
        commit('setProfile', response.data)
      } catch (error) {
        console.error('Unable to fetch profile.')
        dispatch('getAuthState')
      }
    },

    // Get wallet balance.
    async getWalletBalance ({ commit }) {
      try {
        let response = await axios.get('/api/user/wallet-balance')
        commit('setWalletBalance', response.data.balance)
      } catch (error) {
        console.error('Unable to fetch wallet balance.')
      }
    },

    // Get transaction history.
    async getTransactionHistory ({ commit }) {
      try {
        let endpoints = [
          //'/api/user/transaction-history?actionType=ALL',
          '/api/user/transaction-history?actionType=OUTSIDE',
          '/api/user/transaction-history?actionType=FEES',
          '/api/user/transaction-history?actionType=PURCHASES'
        ];

        let response = await Promise.all(endpoints.map((endpoint) => axios.get(endpoint))).then(
          //axios.spread(({data:all}, {data:outside}, {data:fees}, {data:purchases}) => {
          axios.spread(({data:outside}, {data:fees}, {data:purchases}) => {
            //console.log({ all, outside, fees, purchases });
            //console.log(all);

            response = {
              //all: all,
              outside: outside,
              fees: fees,
              purchases: purchases,
            }
            //console.log(response);
            commit('setTransactionHistory', response)
          })
        );
      } catch (error) {
        console.error('Unable to fetch transaction history.')
      }

      /*
      try {
        let response = await axios.get(`/api/user/transaction-history`)
        commit('setTransactionHistory', response.data)
      } catch (error) {
        console.error('Unable to fetch transaction history.')
      }
      */
    },

    // Get user integrations.
    async getUserIntegrations ({ commit }) {
      try {
        let response = await axios.get('/api/user/integration')
        commit('setUserIntegrations', response.data)
      } catch (error) {
        console.error('Unable to fetch user integrations.')
      }
    },

    /**
     * Create user integration
     *
     *  payload = {
     *    data: "string",
     *    externalAccountName: "string",
     *    name: "string"
     *  }
     */
    async createUserIntegration ({ commit, dispatch }, payload) {
      try {
        let response = await axios.post('/api/user/integration', payload)
        commit('setUserIntegration', response.data)
        dispatch('getUserIntegrations')
      } catch (error) {
        console.error('Unable to create user integrations.')
        throw(error)
      }
    },

    // Get plain link token.
    async getUserPlaidLinkToken ({ commit }) {
      try {
        let response = await axios.get('/api/user/plaid-link-token')
        commit('setPlaidLink', response.data)
      } catch (error) {
        console.error('Unable to fetch user integrations.')
      }
    },
  
  },

  // User state getters.
  getters: {
    isAdmin: state => state.user.admin,
    authenticated: state => state.authenticated,
    user: state => state.user,
    profile: state => state.profile,
    userIntegrations: state => state.userIntegrations,
    userIntegration: state => state.userIntegration,
    walletBalance: state => state.walletBalance,
    transactionHistory: state => state.transactionHistory,
    loginRedirect: state => state.loginRedirect,
    plaidLink: state => state.plaidLink,
  }
}
